import { Controller } from "@hotwired/stimulus";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import mapboxgl from "mapbox-gl";

export default class extends Controller {
  static targets = ["bounds", "container", "lat", "lng", "query", "submitButton"];

  connect() {
    this.geocoder = new MapboxGeocoder({
      accessToken: this.data.get("mapboxAccessToken"),
      autocomplete: false,
      countries: "us",
      fuzzyMatch: true,
      mapboxgl: mapboxgl,
      marker: false,
      minLength: 3,
      placeholder: "Search by address or location",
      types: "address,district,locality,neighborhood,place,postcode,region",
    });

    if (window.map) {
      this.containerTarget.appendChild(this.geocoder.onAdd(window.map));
    } else {
      this.geocoder.addTo(this.containerTarget);
    }

    if (this.inputElement) {
      this.inputElement.focus();
      this.inputElement.dispatchEvent(new Event("change"));
    }

    this.geocoder.on("loading", ({ query }) => {
      this.loading = true;
      this.dispatch("loading");
    });

    this.geocoder.on("results", ({ results }) => {
      this.loading = false;
      this.dispatch("results");
    });

    this.geocoder.on("result", ({ result }) => {
      if (result.bbox) {
        const parts = [result.bbox[1], result.bbox[0], result.bbox[3], result.bbox[2]];
        this.bounds = parts.join(",");
      }

      if (result.center) {
        this.lat = result.center[1];
        this.lng = result.center[0];
      }

      if (result.matching_place_name) {
        this.query = result.matching_place_name;
      } else if (result.place_name) {
        this.query = result.place_name;
      } else {
        this.query = result.text;
      }

      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.focus();
      }

      this.loading = false;
      this.dispatch("result");
    });

    /**
     * Clear results container when search is cleared.
     */
    this.geocoder.on("clear", () => {
      this.lat = "";
      this.lng = "";
      this.query = "";
      this.loading = false;
      this.dispatch("clear");
    });

    this.geocoder.on("error", ({ error }) => {
      this.loading = false;
      this.dispatch("error");
    });

    if (this.hasQueryTarget && this.queryTarget.value.length > 0) {
      /**
       * Set the query input from params.
       *
       * Replace once #425 is merged:
       * https://github.com/mapbox/mapbox-gl-geocoder/pull/425/files
       */
      if (this.inputElement) {
        this.inputElement.value = this.queryTarget.value;
      }
    }

    this.addInputListener();
  }

  disconnect() {
    this.containerTarget.innerHTML = "";
    this.removeInputListener();
  }

  addInputListener() {
    if (!this.inputElement) return;
    this.inputElement.addEventListener("input", this.onInput);
  }

  set bounds(value) {
    // if (this.hasBoundsTarget) {
    //   this.boundsTarget.value = value;
    // }
  }

  /**
   * Provide ability to clear lat/lng/address input values. This may be
   * necessary when repositioning the map, otherwise when refreshed the map
   * would return to that lat/lng position instead of the boundary coords.
   */
  clearInputValues() {
    this.lat = "";
    this.lng = "";
    this.query = "";
    this.geocoder.clear();
    this.inputElement.value = "";
  }

  get inputElement() {
    const elements = document.getElementsByClassName("mapboxgl-ctrl-geocoder--input");
    if (elements.length > 0) {
      return elements[0];
    }
    return null;
  }

  set lat(value) {
    if (this.hasLatTarget) {
      this.latTarget.value = value;
      this.latTarget.form.dispatchEvent(new Event("change"));
    }
  }

  set lng(value) {
    if (this.hasLngTarget) {
      this.lngTarget.value = value;
      this.lngTarget.form.dispatchEvent(new Event("change"));
    }
  }

  set loading(isLoading) {
    if (isLoading) {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.setAttribute("loading", "");
      }
    } else {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.removeAttribute("loading");
      }
    }
  }

  onInput = (event) => {
    this.query = event.target.value;
    this.dispatch("input", { detail: { value: event.target.value } });
  };

  set query(value) {
    if (this.hasQueryTarget) {
      this.queryTarget.value = value;
      this.queryTarget.form.dispatchEvent(new Event("change"));
    }
  }

  removeInputListener() {
    if (!this.inputElement) return;
    this.inputElement.revoveEventListener("input", this.onInput);
  }
}
